import {Injectable} from '@angular/core';
import {
	InventoryXLS,
	Language,
	Territory,
	MediaType,
	Inventory,
	ContentCode,
	ProjectCode,
	ProductCode,
	WorkflowStatus,
	InventoryWithPagination,
	ExclusivityNames,
	InventoryExtra,
	RightsLanguageMetadata,
	RightsView,
	Channel,
	Genre,
} from '@entities/rights/rights.interfaces';
import {
	RightsInventoryResponse,
	RightsLanguageResponse,
	RightsTerritoryResponse,
	RightsMediaResponse,
	RightsBrandsResponse,
	RightsProjectTypesResponse,
	RightsProductOwnersResponse,
	RightsWorkflowStatusResponse,
	RightsInventoryPaginationResponse,
	RightsInventoryResponseSublicenseNote,
	RightsInventoryResponseExtra,
	RightsLanguageMetadataResponse,
	RightsInventoryRequest,
	RightsChannelResponse,
	RightsGenreResponse,
} from '@entities/rights/rights-api-interfaces';
import {MapTreeHelper} from '@shared/helpers/map-tree.helper';
import {defineDate, defineUTCDate} from '@shared/helpers/format-data.helper';
import {MasterType} from '@shared/enums/master-type.enum';
import {v4 as uuidv4} from 'uuid';
// const MDM_NAMESPACE = 'ed676928-4e92-45a4-b3f3-c6dfae5b2833';

/**
 * This class is an injectable Angular service that provides a way to fetch data..
 */
@Injectable()
export class RightsApiMapper {
	/**
	 * Static method Map Master Type for mapped data from API response.
	 * @param {RightsInventoryResponse['LEVEL_CODE']} data - param for master type.
	 * @returns {MasterType} - the mapped data for API response.
	 */
	public static mapMasterType(data: RightsInventoryResponse['LEVEL_CODE']): MasterType {
		const lower = (data || '').toLowerCase();
		if (lower === 'series') {
			return MasterType.Season;
		}
		return lower as MasterType;
	}

	/** Auxiliary 'snakeToCamel' arrow function.
	 * @param str */
	public static snakeToCamel = (str) =>
		str.toLowerCase().replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace('-', '').replace('_', ''));

	/**
	 * Static method Map Languages for mapped data from API response.
	 * @param {RightsLanguageResponse[]} data - param for languages.
	 * @returns {Language[]} - the mapped data for API response.
	 */
	public static mapLanguages(data: RightsLanguageResponse[]): Language[] {
		return MapTreeHelper.buildTree(data, 'LANGUAGE_ID', 'LANGUAGE_NAME', 'LANGUAGE_CODE');
	}

	/**
	 * Static method Map Channels for mapped data from API response.
	 * @param {RightsChannelResponse[]} data - param for channels.
	 * @returns {Channel[]} - the mapped data for API response.
	 */
	public static mapChannels(data: RightsChannelResponse[]): Channel[] {
		return MapTreeHelper.buildTree(data, 'CHANNEL_ID', 'CHANNEL_NAME', 'CHANNEL_CODE', null, false);
	}

	/**
	 * Static method Map Genres for mapped data from API response.
	 * @param {RightsChannelResponse[]} data - param for genres.
	 * @returns {Genre[]} - the mapped data for API response.
	 */
	public static mapGenres(data: RightsGenreResponse[]): Genre[] {
		return MapTreeHelper.buildTree(data, 'GENRE_ID', 'GENRE_NAME', 'GENRE_CODE', null, false);
	}

	/**
	 * Static method Map Languages Metadata for mapped data from API response.
	 * @param {Record<string, RightsLanguageMetadataResponse[]>} data
	 * @returns {Record<string, RightsLanguageMetadata[]>}
	 */
	public static mapLanguagesMetadata(
		data: Record<string, RightsLanguageMetadataResponse[]>
	): Record<string, RightsLanguageMetadata[]> {
		return data;
	}

	/**
	 * Static method Map Territories for mapped data from API response.
	 * @param {RightsTerritoryResponse[]} data - param for territories.
	 * @returns {Territory[]} - the mapped data for API response.
	 */
	public static mapTerritories(data: RightsTerritoryResponse[]): Territory[] {
		return MapTreeHelper.buildTree(data, 'TERRITORY_ID', 'TERRITORY_NAME', 'TERRITORY_CODE');
	}

	/**
	 * Static method Map Media Types for mapped data from API response.
	 * @param {RightsMediaResponse[]} data - param for media types.
	 * @returns {MediaType[]} - the mapped data for API response.
	 */
	public static mapMediaTypes(data: RightsMediaResponse[]): MediaType[] {
		return MapTreeHelper.buildTree(data, 'MEDIA_ID', 'MEDIA_NAME', 'MEDIA_CODE');
	}

	/**
	 * Static method Map Brands for mapped data from API response.
	 * @param {RightsBrandsResponse[]} data - param for brands.
	 * @returns {ContentCode[]} - the mapped data for API response.
	 */
	public static mapBrands(data: RightsBrandsResponse[]): ContentCode[] {
		const newData = data.map((item) => ({
			BRAND_ID: String(item.BRAND_ID),
			CONTENT_CODE: String(item.CONTENT_CODE),
			BRAND_NAME: item.BRAND_NAME,
			LEVEL_ID: item.LEVEL_ID,
			PARENT_ID: String(item.PARENT_ID),
		}));

		return MapTreeHelper.buildTree(newData, 'BRAND_ID', 'BRAND_NAME', 'CONTENT_CODE');
	}

	/**
	 * Static method Map Project Types for mapped data from API response.
	 * @param {RightsProjectTypesResponse[]} data - param for project types.
	 * @returns {ProjectCode[]} - the mapped data for API response.
	 */
	public static mapProjectTypes(data: RightsProjectTypesResponse[]): ProjectCode[] {
		const newData = data.map((item) => ({
			FILTER_ID: String(item.FILTER_ID),
			PROJECT_TYPE_DESC: String(item.PROJECT_TYPE_DESC),
			LEVEL_ID: item.LEVEL_ID,
			PARENT_ID: String(item.PARENT_ID),
		}));

		return MapTreeHelper.buildTree(newData, 'FILTER_ID', 'PROJECT_TYPE_DESC', 'PROJECT_TYPE_DESC');
	}

	/**
	 * Static method Map Product Owners for mapped data from API response.
	 * @param {RightsProductOwnersResponse[]} data - param for product owners.
	 * @returns {ProductCode[]} - the mapped data for API response.
	 */
	public static mapProductOwners(data: RightsProductOwnersResponse[]): ProductCode[] {
		const newData = data.map((item) => ({
			FILTER_ID: String(item.FILTER_ID),
			PRODUCT_OWNER_DESC: String(item.PRODUCT_OWNER_DESC),
			LEVEL_ID: item.LEVEL_ID,
			PARENT_ID: String(item.PARENT_ID),
		}));

		return MapTreeHelper.buildTree(newData, 'FILTER_ID', 'PRODUCT_OWNER_DESC', 'PRODUCT_OWNER_DESC');
	}

	/**
	 * Static method Map Workflow Statuses for mapped data from API response.
	 * @param {RightsWorkflowStatusResponse[]} data - param for workflow statuses.
	 * @returns {WorkflowStatus[]} - the mapped data for API response.
	 */
	public static mapWorkflowStatuses(data: RightsWorkflowStatusResponse[]): WorkflowStatus[] {
		const itemAll = {
			FILTER_ID: '999',
			LEVEL_ID: 1,
			PARENT_ID: '0',
			WORKFLOW_STATUS: 'All Workflow Statuses',
		};

		const mappedData = data.map((item) => ({
			FILTER_ID: String(item.WORKFLOW_STATUS_ID),
			WORKFLOW_STATUS: String(item.WORKFLOW_STATUS),
			LEVEL_ID: 2,
			PARENT_ID: '999',
		}));

		const newData = [itemAll, ...mappedData];

		return MapTreeHelper.buildTree(newData, 'FILTER_ID', 'WORKFLOW_STATUS', 'WORKFLOW_STATUS');
	}

	/**
	 * Static method Map Inventory Item for mapped data from API response.
	 * @param {RightsInventoryResponse & {flagTooltip?: string; calcDate?: string; extra?: InventoryExtra[]}} item - param for inventory item.
	 * @returns {Inventory & Record<string, string | number>} - the mapped data for API response.
	 */
	public static mapInventoryItem(
		item: RightsInventoryResponse & {flagTooltip?: string; calcDate?: string; extra?: InventoryExtra[]}
	): Inventory & Record<string, string | number> {
		const inventory = {
			assetId: !item.OBJECT_CODE || isNaN(+item.OBJECT_CODE) ? item.OBJECT_CODE : +item.OBJECT_CODE,
			castAndCrew: item.CAST_AND_CREW,
			channel: item.CHANNEL_BRANDS,
			channelBrands: item.CHANNEL_BRANDS,
			content: item.OBJECT_NAME,
			dateEnd: defineDate(item.ACTUAL_TERM_END),
			dateEndUndefined: item.ACTUAL_TERM_END_IS_UNDEFINED === 1 ? 1 : 0,
			dateStart: defineDate(item.ACTUAL_TERM_START),
			specialDateStart: defineDate(item.calcDate),
			isDateStartSpecial: +!item.ACTUAL_TERM_START,
			dateStartUndefined: item.ACTUAL_TERM_START_IS_UNDEFINED === 1 ? 1 : 0,
			dealId: item.DEAL_ID || '—',
			dealName: item.DEAL_NAME || '—',
			durationMin: item.DURATION_MIN,
			episodes: item.EPISODES || '—',
			exclusivity: ExclusivityNames[String(item.EXCLUSIVITY_VALUE).toLowerCase()],
			genreCode: item.GENRE_CODE || '—',
			id: item.OBJECT_ID,
			keywords: item.KEYWORDS,
			language: item.LANGUAGE,
			channels: item.CHANNELS,
			genres: item.GENRE_CODE,
			levelCodeInitial: item.LEVEL_CODE,
			levelCode: RightsApiMapper.mapMasterType(item.LEVEL_CODE),
			licensee: item.LICENSEE || '—',
			licensor: item.LICENSOR || '—',
			linkIdsAMCN:
				!item.ASSET_IDS_AMCN || isNaN(+item.ASSET_IDS_AMCN) ? item.ASSET_IDS_AMCN : +item.ASSET_IDS_AMCN,
			linkIdsRL: !item.ASSET_IDS_RL || isNaN(+item.ASSET_IDS_RL) ? item.ASSET_IDS_RL : +item.ASSET_IDS_RL,
			linkIdsRLJE:
				!item.ASSET_IDS_RLJE || isNaN(+item.ASSET_IDS_RLJE) ? item.ASSET_IDS_RLJE : +item.ASSET_IDS_RLJE,
			media: item.MEDIA,
			notes: item.NOTES,
			numOfEpisodes: item.NUM_OF_EPISODES,
			numOfSeason: item.NUM_OF_SEASON,
			productOwnerCode: item.PRODUCT_OWNER_CODE || '—',
			projectTypeCode: item.PROJECT_TYPE_CODE || '—',
			rowNumOrder: item.ROWNUM_ORDER,
			seasons: item.SEASONS || '—',
			shortSynopsys: item.SHORT_SYNOPSIS,
			templateName: item.TEMPLATENAME,
			territory: item.TERRITORY,
			uniqueId: uuidv4(),
			flagTooltip: item.flagTooltip,
			extra: item.extra,
		};

		const keys = Object.keys(inventory);
		const itemKeys = Object.keys(item);

		// it needs for pinned data
		itemKeys.forEach((itemKey) => {
			const key = RightsApiMapper.snakeToCamel(itemKey);
			if (!keys.includes(key)) {
				inventory[key] = item[itemKey];
			}
		});

		return inventory as Inventory & Record<string, string | number>;
	}

	/**
	 * Static method Map Inventory for mapped data from API response.
	 * @param {[
			RightsInventoryResponse[],
			any,
			RightsInventoryPaginationResponse[],
			RightsInventoryResponseSublicenseNote[],
			RightsInventoryResponseExtra[]
		]} data - param for inventory.
	 * @returns {InventoryWithPagination} - the mapped data for API response.
	 */
	public static mapInventory(
		params: RightsInventoryRequest,
		data: {
			docs: [
				RightsInventoryResponse[],
				any,
				RightsInventoryPaginationResponse[],
				RightsInventoryResponseSublicenseNote[],
				RightsInventoryResponseExtra[]
			];
		}
	): InventoryWithPagination {
		const {docs} = data;
		const isRightsIn = params.MODE === RightsView.RightsIn;
		return {
			data: docs?.[0].map((el) =>
				RightsApiMapper.mapInventoryItem({
					...el,
					flagTooltip: RightsApiMapper.mapSublicenseNote(el, docs?.[3]),
					extra: RightsApiMapper.mapInventoryExtra(
						el,
						isRightsIn ? (docs?.[3] as unknown as RightsInventoryResponseExtra[]) : docs?.[4],
						isRightsIn
					),
					calcDate: docs[2][0].CALC_DATE,
				})
			),
			pagination: {
				pageNum: docs?.[2][0].PAGE_NUM,
				totalPages: docs?.[2][0].TOTAL_PAGES ?? 100,
			},
		};
	}

	/**
	 * Static method Map Inventory X L S for mapped data from API response.
	 * @param {RightsInventoryResponse[]} data - param for inventory x l s.
	 * @returns {InventoryXLS[]} - the mapped data for API response.
	 */
	public static mapInventoryXLS(data: RightsInventoryResponse[]): InventoryXLS[] {
		return data.map((item) => ({
			...RightsApiMapper.mapInventoryItem(item),
			dateStartObject: defineUTCDate(item.ACTUAL_TERM_START),
			dateEndObject: defineUTCDate(item.ACTUAL_TERM_END),
		}));
	}

	/**
	 * Static method Map Inventory Extra for mapped data from API response.
	 * @param {RightsInventoryResponse} item - param for inventory extra.
	 * @param {RightsInventoryResponseExtra[]} extraData - param for inventory extra.
	 * @returns {InventoryExtra[]} - the mapped data for API response.
	 */
	public static mapInventoryExtra(
		item: RightsInventoryResponse,
		extraData: RightsInventoryResponseExtra[],
		isRightsIn: boolean = false
	): InventoryExtra[] {
		return extraData
			?.filter(({LEVEL_CODE: level, OBJECT_ID: id}) => level === item.LEVEL_CODE && id === +item.OBJECT_ID)
			?.map((el) => {
				if (isRightsIn) {
					return {
						elementCode: el.ELEMENT_CODE,
						elementName: el.ELEMENT_NAME,
					};
				}
				return {
					inventoryAmcnId: el.INVENTORY_AMCN_ID || '—',
					inventoryTitle: el.INVENTORY_TITLE || '—',
					sellOfEndDate: defineDate(el.SELL_OFF_PERIOD_END_DATE) || '—',
					sku: el.SKU || '—',
					upc: el.UPC || '—',
				};
			});
	}

	/**
	 * Static method Map Sublicense Note for mapped data from API response.
	 * @param {RightsInventoryResponse} item - param for sublicense note.
	 * @param {RightsInventoryResponseSublicenseNote[]} directory - param for sublicense note.
	 * @returns {string} - the mapped data for API response.
	 */
	private static mapSublicenseNote(
		item: RightsInventoryResponse,
		directory: RightsInventoryResponseSublicenseNote[]
	): string {
		const {CONTRACT_NO_VERSION: versions, SUBLICENSE_RED_FLAG_NOTE: note, DEAL_ID: id} = item;

		if (!versions && !note) return null;

		const rows = [...new Set((versions ?? '')?.split(';'))]
			.map(
				(el) =>
					`<tr>${versions ? `<td>${el}:</td>` : ''}<td>${
						(versions
							? directory.find(({CONTRACT_NO_VERSION: name}) => name === el)?.SUBLICENSE_RED_FLAG_NOTE
							: note) ?? 'N/A'
					}</td></tr>`
			)
			.join('');

		return `<table class="tippy-table">${rows}</table>`;
	}
}
